import React from 'react';
import {
  AlertTriangle, Moon, Zap, Trophy, Play, ChevronRight,
} from 'lucide-react';

const DOCTOR_IMG = 'https://images.unsplash.com/photo-1645066928295-2506defde470?w=300&q=80';

const AIDoctorCard = ({ aiMsg, userState, navigate, testJustCompleted, completedTestName }) => (
  <>
    {/* Post-test celebration banner */}
    {testJustCompleted && (
      <div className="relative overflow-hidden rounded-[20px] p-5" style={{
        background: 'linear-gradient(160deg, rgba(16,185,129,0.12), rgba(10,18,38,0.85))',
        border: '1px solid rgba(52,211,153,0.35)',
        boxShadow: '0 0 24px rgba(52,211,153,0.2), inset 0 0 20px rgba(52,211,153,0.05)',
        animation: 'fadeSlideIn 0.5s ease-out',
      }} data-testid="test-celebration-banner">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{
            background: 'rgba(52,211,153,0.15)', border: '1px solid rgba(52,211,153,0.3)',
            boxShadow: '0 0 14px rgba(52,211,153,0.3)',
          }}>
            <Trophy className="w-5 h-5" style={{ color: '#34D399', filter: 'drop-shadow(0 0 6px rgba(52,211,153,0.7))' }} />
          </div>
          <div>
            <p style={{ color: '#6EE7B7', fontSize: '16px', fontWeight: 700 }}>Tebrikler! {completedTestName} tamamlandı.</p>
            <p style={{ color: '#AAB4C5', fontSize: '13px', marginTop: '2px' }}>Sonuçlar doktoruna iletildi. Aşağıda yeni değerlendirmeni görebilirsin.</p>
          </div>
        </div>
      </div>
    )}

    {/* AI Doctor Card */}
    <div className="relative overflow-hidden" style={{
      background: 'linear-gradient(180deg, rgba(10,18,38,0.15), rgba(8,14,32,0.22))',
      backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
      borderRadius: '24px',
      border: '1px solid rgba(255,255,255,0.10)',
      boxShadow: '0 0 50px rgba(34,211,238,0.15), 0 20px 60px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.06)',
      padding: '28px',
    }} data-testid="hero-section">
      {/* Star dots */}
      <div className="absolute inset-0 pointer-events-none" style={{
        zIndex: 0,
        backgroundImage:
          'radial-gradient(circle, rgba(255,255,255,0.60) 0px, rgba(255,255,255,0.60) 1px, transparent 2px), radial-gradient(circle, rgba(125,211,252,0.50) 0px, rgba(125,211,252,0.50) 1.2px, transparent 2.2px), radial-gradient(circle, rgba(196,181,253,0.45) 0px, rgba(196,181,253,0.45) 1px, transparent 2px), radial-gradient(circle, rgba(255,255,255,0.40) 0px, rgba(255,255,255,0.40) 1.5px, transparent 2.5px), radial-gradient(circle, rgba(245,158,11,0.35) 0px, rgba(245,158,11,0.35) 1px, transparent 2px)',
        backgroundSize: '100px 80px, 150px 110px, 180px 130px, 220px 170px, 260px 200px',
        backgroundPosition: '10px 15px, 60px 40px, 120px 20px, 30px 70px, 170px 55px',
        filter: 'drop-shadow(0 0 4px rgba(255,255,255,0.25)) drop-shadow(0 0 8px rgba(125,211,252,0.15))',
        opacity: 0.85,
        animation: 'subtleTwinkle 6s ease-in-out infinite',
      }} />
      <div className="flex gap-5">
        {/* Avatar */}
        <div className="relative shrink-0" data-testid="ai-avatar">
          <div className="absolute -inset-4 rounded-full" style={{ background: 'radial-gradient(circle, rgba(34,211,238,0.2), transparent 70%)' }} />
          <div className="relative w-20 h-20 rounded-xl overflow-hidden" style={{ border: '2px solid rgba(34,211,238,0.2)', boxShadow: '0 0 30px rgba(34,211,238,0.2)' }}>
            <img src={DOCTOR_IMG} alt="Doktor Asistan" className="w-full h-full object-cover object-top" />
            <div className="absolute bottom-1 right-1 w-3 h-3 bg-emerald-400 rounded-full border-2 border-[#0f172a]" style={{ boxShadow: '0 0 8px rgba(52,211,153,0.6)' }} />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h2 className="font-bold text-white" style={{ fontSize: '16px', letterSpacing: '-0.01em', lineHeight: 1.2 }} data-testid="doctor-name">Doktorun</h2>
            <span className="w-2 h-2 bg-emerald-400 rounded-full" style={{ boxShadow: '0 0 6px rgba(52,211,153,0.6)' }} />
          </div>

          {aiMsg.state_feeling && (
            <p className="font-bold mb-2" style={{ color: '#67E8F9', fontSize: '18px', lineHeight: 1.25, letterSpacing: '-0.02em' }} data-testid="state-feeling">{aiMsg.state_feeling}</p>
          )}

          <p className="leading-[1.65] mb-4" style={{ color: '#D6DCE8', maxWidth: '65ch', fontSize: '14px' }} data-testid="ai-message-card">
            {aiMsg.message || 'Verileriniz analiz ediliyor...'}
          </p>

          {/* Status tags */}
          {userState?.domain_scores && (
            <div className="flex items-center gap-[10px] mt-4 mb-4" style={{ flexWrap: 'nowrap' }} data-testid="doctor-status-tags">
              {(() => {
                const scores = userState.domain_scores;
                const getLevel = (score) => {
                  if (score == null) return null;
                  if (score >= 50) return 'Belirgin';
                  if (score >= 25) return 'Orta';
                  if (score >= 10) return 'Hafif';
                  return 'Düşük';
                };
                const tags = [
                  { icon: AlertTriangle, label: 'Kaygı', score: scores.kaygi, color: '#FCD34D', bg: 'rgba(245,158,11,0.16)', border: 'rgba(245,158,11,0.24)', glow: '0 0 16px rgba(245,158,11,0.12)' },
                  { icon: Moon, label: 'Uyku', score: scores.uyku, color: '#67E8F9', bg: 'rgba(56,189,248,0.16)', border: 'rgba(56,189,248,0.24)', glow: '0 0 16px rgba(56,189,248,0.12)' },
                  { icon: Zap, label: 'Enerji', score: scores.enerji, color: '#FDBA74', bg: 'rgba(251,146,60,0.16)', border: 'rgba(251,146,60,0.24)', glow: '0 0 16px rgba(251,146,60,0.12)' },
                ].filter(t => t.score != null);
                return tags.map((tag, i) => (
                  <div key={i} className="inline-flex items-center gap-1.5 rounded" style={{
                    padding: '6px 10px',
                    background: tag.bg, border: `1px solid ${tag.border}`,
                    boxShadow: tag.glow,
                    fontSize: '12px', fontWeight: 600, color: tag.color,
                    lineHeight: 1, whiteSpace: 'nowrap',
                  }}>
                    <tag.icon className="w-3.5 h-3.5" />
                    {tag.label}: {getLevel(tag.score)}
                  </div>
                ));
              })()}
            </div>
          )}

          {/* CTA block */}
          {aiMsg.next_action_message && (
            <div className="mt-5 pt-4" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
              <p style={{ color: '#D6DCE8', fontSize: '14px', lineHeight: 1.6, marginBottom: '14px' }} data-testid="doctor-cta-message">
                {aiMsg.next_action_message}
              </p>
              {userState?.recommended_test?.test_id && (
                <button
                  onClick={() => {
                    const testId = userState.recommended_test.test_id;
                    if (testId === '__cardio__') {
                      window.location.href = '/cardio';
                    } else {
                      window.location.href = `/test/${testId}`;
                    }
                  }}
                  className="flex items-center justify-center gap-2 rounded-[14px] transition-all duration-300 hover:scale-[1.02]"
                  style={{
                    width: '100%', minHeight: '48px',
                    background: 'rgba(34,211,238,0.10)',
                    border: '1px solid rgba(34,211,238,0.5)',
                    color: '#67E8F9',
                    fontSize: '15px', fontWeight: 700, letterSpacing: '0.2px',
                    boxShadow: '0 0 18px rgba(34,211,238,0.4), 0 0 40px rgba(34,211,238,0.15), inset 0 0 12px rgba(34,211,238,0.1)',
                    animation: 'ctaPulse 3s ease-in-out infinite',
                  }}
                  data-testid="doctor-cta-button"
                >
                  <Play className="w-4 h-4" /> Şimdi Başla
                </button>
              )}
            </div>
          )}
          {!aiMsg.next_action_message && (
            <p className="mt-3" style={{ color: '#94A3B8', fontSize: '13px', fontWeight: 500 }}>Bugün için önerilen adım aşağıda hazır.</p>
          )}
        </div>
      </div>

      <p className="text-white/15 text-[9px] mt-3 text-center">Bu değerlendirme tanı yerine geçmez.</p>

      <button
        onClick={() => navigate('/ai-doctor')}
        className="w-full mt-4 flex items-center justify-center gap-2 rounded-[14px] transition-all duration-300 hover:scale-[1.02]"
        style={{
          minHeight: '44px',
          background: 'rgba(255,255,255,0.05)',
          border: '1px solid rgba(255,255,255,0.1)',
          backdropFilter: 'blur(10px)',
          color: '#ffffff', fontSize: '14px', fontWeight: 500,
          boxShadow: '0 0 10px rgba(0, 200, 255, 0.3)',
        }}
        onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 0 20px rgba(0, 200, 255, 0.6)'; }}
        onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 0 10px rgba(0, 200, 255, 0.3)'; }}
        data-testid="doctor-detail-button"
      >
        Doktorunun Tam Değerlendirmesini Gör <ChevronRight className="w-4 h-4" />
      </button>

      <div className="absolute bottom-0 left-0 right-0 h-[2px]" style={{
        background: 'linear-gradient(90deg, transparent, #22D3EE, transparent)', filter: 'blur(1px)',
      }} />
    </div>
  </>
);

export default AIDoctorCard;
